import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seoApi, type AuditStatus } from '../api/seoApi';
import { useLocation } from '@/shared/hooks/useLocation';
import { useToast } from '@/shared/ui/Toast';
import { seoKeys } from '../queryKeys';

export function useAuditOverview() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: seoKeys.overview(locationId),
    queryFn: () => seoApi.getOverview(locationId).then((r) => r.data),
    enabled: !!locationId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.isRunning ? 15_000 : false;
    },
  });
}

export function useLatestAudit() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: seoKeys.auditLatest(locationId),
    queryFn: () => seoApi.getLatestAudit(locationId).then((r) => r.data),
    enabled: !!locationId,
  });
}

export function useAuditStatus() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: seoKeys.auditStatus(locationId),
    queryFn: () => seoApi.getAuditStatus(locationId).then((r) => r.data),
    enabled: !!locationId,
    staleTime: 5_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'in_progress' ? 5_000 : false;
    },
  });
}

export function useRefreshAudit() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    // recrawl=true — "Refresh Audit" on the Site Audit page always re-crawls the
    // website rather than re-running the audit against stale cached HTML.
    mutationFn: () => seoApi.triggerAudit(locationId, 'manual', true, true, true),
    onSuccess: (res) => {
      if (res.data?.queued === false) {
        toast.info(res.data.message ?? 'Audit already in progress');
        return;
      }
      toast.success('Audit started — results in ~5-10 minutes');
      // Optimistically flip to 'pending' so useAuditStatus's refetchInterval engages
      // immediately. Deliberately NOT calling invalidateQueries for auditStatus here —
      // the job runs async in a worker, so an immediate refetch would very likely land
      // before the worker creates the new SiteAnalysis row, still see the PREVIOUS
      // run's terminal status, clobber this optimistic update, and — since
      // refetchInterval only continues while status is pending/in_progress — stop
      // polling for good (the panel would then only reappear on a manual page reload).
      // The 5s poll loop that this optimistic 'pending' state kicks off is what
      // eventually picks up the real in-progress data once the worker catches up.
      queryClient.setQueryData<AuditStatus | undefined>(seoKeys.auditStatus(locationId), (prev) =>
        prev ? { ...prev, status: 'pending', progress: null } : prev,
      );
      queryClient.invalidateQueries({ queryKey: seoKeys.overview(locationId) });
    },
    onError: () => toast.error('Failed to start audit'),
  });
}

export function useCancelAudit() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: () => seoApi.cancelAudit(locationId),
    onSuccess: () => {
      toast.info('Audit cancelled');
      queryClient.invalidateQueries({ queryKey: seoKeys.overview(locationId) });
      queryClient.invalidateQueries({ queryKey: seoKeys.auditStatus(locationId) });
    },
    onError: () => toast.error('Failed to cancel audit'),
  });
}

export function useSiteHealth() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: seoKeys.siteHealth(locationId),
    queryFn: () => seoApi.getSiteHealth(locationId).then((r) => r.data),
    enabled: !!locationId,
  });
}

export function useContentOpportunities() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  return useQuery({
    queryKey: seoKeys.content(locationId),
    queryFn: () => seoApi.getContent(locationId).then((r) => r.data),
    enabled: !!locationId,
  });
}
